import { SearchState } from "@/contexts/SearchContext/interfaces";
import { Property } from "@/interfaces/property.interface";
import { gTagEvents } from "@/lib/gtag";
import { getListingId } from "@/lib/gtag/helpers";
import { useCallback, useEffect, useRef, useState } from "react";

export const usePropertyView = (
  state: SearchState,
  properties?: Array<Property>,
  defaultListingId?: string
) => {
  const {
    sort,
    listing: { type }, // ignoring property
    ...remainingState
  } = state;

  const viewedPropertiesRef = useRef<Array<Property>>([]);
  const viewingPropertiesRef = useRef<Array<Property>>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [triggerEvent, setTriggerEvent] = useState<"sort" | "type" | "general">(
    "general"
  );
  const [listingId, setListingId] = useState<string>(() =>
    getListingId(state, triggerEvent)
  );

  // Simple debounce function
  const debouncedGTagEvent = (
    listingId: string,
    properties: Array<Property>
  ) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      gTagEvents.viewPropertyList(
        defaultListingId ?? listingId,
        properties,
        state
      );
    }, 500);
  };

  const handleCardView = useCallback(
    (property: Property, viewState: "off" | "on") => {
      if (viewState === "off") {
        if (
          viewingPropertiesRef.current.find(
            (_property) => _property.id === property.id
          )
        ) {
          viewedPropertiesRef.current = [
            ...viewedPropertiesRef.current,
            property,
          ];
          viewingPropertiesRef.current = viewingPropertiesRef.current.filter(
            (_property) => _property.id !== property.id
          );
        }
      } else {
        // Only add to viewing properties if not already viewed
        if (
          !viewedPropertiesRef.current.map((p) => p.id).includes(property.id)
        ) {
          viewingPropertiesRef.current = [
            ...viewingPropertiesRef.current,
            property,
          ];
        }
      }

      // Trigger debounced analytics if we have properties to report
      if (viewingPropertiesRef.current.length > 0 && listingId) {
        debouncedGTagEvent(listingId, viewingPropertiesRef.current);
      }
    },
    [state, listingId, viewedPropertiesRef]
  );

  const handleCardClick = useCallback(
    (property: Property, i: number) => {
      gTagEvents.selectProperty(property, listingId!, state, i);
    },
    [state, listingId]
  );

  // Update listingId when state changes
  useEffect(() => {
    setListingId(getListingId(state, triggerEvent));
  }, [state, triggerEvent]);

  // Update triggerEvent based on different state changes
  useEffect(() => {
    setTriggerEvent("sort");
  }, [sort]);

  useEffect(() => {
    setTriggerEvent("type");
  }, [type]);

  useEffect(() => {
    setTriggerEvent("general");
  }, [JSON.stringify(remainingState)]);

  // Reset when properties change
  useEffect(() => {
    viewedPropertiesRef.current = [];
    viewingPropertiesRef.current = [];

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [properties, timeoutRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    handleCardClick,
    handleCardView,
    listingId: defaultListingId ?? listingId,
  };
};
